// src/pages/buyers/BuyerDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  Rating,
  LinearProgress,
  Fade,
  Zoom,
  CardActions,
  Badge,
  Stack,
  Skeleton,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  useMemo as useMemoMui,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Dashboard as DashboardIcon,
  Storefront as StorefrontIcon,
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  LocalShipping as LocalShippingIcon,
  Business as BusinessIcon,
  Money as MoneyIcon,
  Pending as PendingIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  WhatsApp as WhatsAppIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  // ADD THIS LINE ↓↓↓
  ShoppingBag as ShoppingBagIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Receipt as ReceiptIcon,
  Store as StoreIcon,
  Assessment as AssessmentIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  List as ListIcon,
  GridView as GridIcon,
} from '@mui/icons-material';

// ============================================
// CHART IMPORTS - Lazy loaded for performance
// ============================================
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';

// ============================================
// IMPORT SHARED COMPONENTS
// ============================================
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { BRAND_COLORS, StatusChip } from './components/styles';
import { formatCurrency, formatDate } from './components/utils';

// ============================================
// ENHANCED COLOR SCHEME
// ============================================
const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  whatsapp: '#25D366',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// ============================================
// API CONFIGURATION
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const API_ENDPOINTS = {
  PRODUCTS: {
    LIST: `${API_BASE_URL}/products/buyer-products.php`,
  },
  ORDERS: {
    LIST: `${API_BASE_URL}/orders/orders_.php`,
  },
  BUYER: {
    PROFILE: `${API_BASE_URL}/buyers/profile.php`,
  },
  WISHLIST: {
    GET: `${API_BASE_URL}/wishlist/get.php`,
  },
};

// ============================================
// LOGGER
// ============================================
const logger = {
  info: (message, data) => console.log(`[BUYER-DASH] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[BUYER-DASH] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[BUYER-DASH] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[BUYER-DASH] ⚠️ ${message}`, data || ''),
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : IMAGE_BASE_URL + '/';
  return `${base}${imagePath}`;
};

const getStatusColor = (status) => {
  const colors = {
    'pending': COLORS.warning,
    'processing': COLORS.info,
    'shipped': '#6A1B9A',
    'delivered': COLORS.success,
    'cancelled': COLORS.error,
    'paid': COLORS.success,
    'failed': COLORS.error,
  };
  return colors[status] || COLORS.grey[600];
};

const getStatusLabel = (status) => {
  const labels = {
    'pending': 'Pending',
    'processing': 'Processing',
    'shipped': 'Shipped',
    'delivered': 'Delivered',
    'cancelled': 'Cancelled',
    'paid': 'Paid',
    'failed': 'Failed',
  };
  return labels[status] || status || 'N/A';
};

// ============================================
// CUSTOM COMPONENTS - Optimized with React.memo
// ============================================
const GradientStatCard = React.memo(({ icon, title, value, subtitle, color, trend, trendValue, progress }) => (
  <Zoom in style={{ transitionDelay: '100ms' }}>
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 4,
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 40px ${color}25`,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="caption" sx={{ color: COLORS.grey[600], fontWeight: 700, letterSpacing: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: color, fontSize: { xs: '1.8rem', sm: '2rem' } }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: COLORS.grey[600], display: 'block', mt: 0.5, fontWeight: 500 }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                {trend === 'up' ? (
                  <ArrowUpwardIcon sx={{ fontSize: 14, color: COLORS.success }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 14, color: COLORS.error }} />
                )}
                <Typography variant="caption" sx={{ 
                  color: trend === 'up' ? COLORS.success : COLORS.error,
                  fontWeight: 700,
                }}>
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.15),
              color: color,
              width: 56,
              height: 56,
              border: `2px solid ${alpha(color, 0.15)}`,
              '& .MuiSvgIcon-root': { fontSize: 28 },
            }}
          >
            {icon}
          </Avatar>
        </Box>
        {progress !== undefined && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 2,
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(color, 0.1),
              '& .MuiLinearProgress-bar': {
                bgcolor: color,
                borderRadius: 3,
              },
            }}
          />
        )}
      </CardContent>
    </Card>
  </Zoom>
));

const ProductCardComponent = React.memo(({ product, onAddToCart, onToggleWishlist, isInWishlist, onView }) => (
  <Zoom in style={{ transitionDelay: '100ms' }}>
    <Card
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        border: `1px solid ${COLORS.grey[200]}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: '#FFFFFF',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.08)',
          borderColor: COLORS.primary,
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Box
          component="img"
          src={getImageUrl(product.image) || `https://picsum.photos/seed/${product.id}/200/200`}
          alt={product.name}
          sx={{
            width: '100%',
            height: 200,
            objectFit: 'cover',
            bgcolor: COLORS.grey[100],
          }}
          onError={(e) => {
            e.target.src = `https://picsum.photos/seed/${product.id}/200/200`;
          }}
        />
        {product.discounts > 0 && (
          <Chip
            label={`${product.discounts}% OFF`}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              bgcolor: COLORS.error,
              color: 'white',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          />
        )}
        {product.quantity_in_stock < 10 && product.quantity_in_stock > 0 && (
          <Chip
            label={`Only ${product.quantity_in_stock} left`}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              bgcolor: COLORS.warning,
              color: 'white',
              fontWeight: 700,
            }}
          />
        )}
        <IconButton
          onClick={() => onToggleWishlist(product)}
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            bgcolor: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)',
            '&:hover': { bgcolor: 'white' },
          }}
        >
          {isInWishlist(product.id) ? (
            <FavoriteIcon sx={{ color: COLORS.error }} />
          ) : (
            <FavoriteBorderIcon />
          )}
        </IconButton>
      </Box>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={product.productType || 'General'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.6rem',
              bgcolor: alpha(COLORS.info, 0.08),
              color: COLORS.info,
              fontWeight: 600,
            }}
          />
          {product.status === 'active' && (
            <Chip
              label="In Stock"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                bgcolor: alpha(COLORS.success, 0.08),
                color: COLORS.success,
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <Typography 
          variant="subtitle1" 
          fontWeight={700} 
          sx={{ 
            color: COLORS.grey[900],
            fontSize: { xs: '0.95rem', sm: '1rem' },
            lineHeight: 1.3,
            minHeight: 48,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {product.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Rating value={parseFloat(product.average_rating || 0)} size="small" readOnly precision={0.5} />
          <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 500 }}>
            ({product.total_reviews || 0})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.primary, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {formatCurrency(product.discountedPrice || product.price, product.currency)}
          </Typography>
          {product.discounts > 0 && (
            <Typography variant="caption" sx={{ color: COLORS.grey[500], textDecoration: 'line-through', fontWeight: 500 }}>
              {formatCurrency(product.price, product.currency)}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: COLORS.grey[500] }}>
            /{product.unit_of_measure || 'unit'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <BusinessIcon sx={{ fontSize: 12, color: COLORS.grey[400] }} />
          <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 500 }}>
            {product.ManufacturerName || product.supplier_name || 'Various Suppliers'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocationOnIcon sx={{ fontSize: 12, color: COLORS.grey[400] }} />
          <Typography variant="caption" sx={{ color: COLORS.grey[500] }}>
            {product.CountryOrigin || 'N/A'}
          </Typography>
        </Box>
      </CardContent>
      <CardActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
        <Button
          variant="contained"
          fullWidth
          disabled={product.status !== 'active' || product.quantity_in_stock <= 0}
          onClick={() => onAddToCart(product)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            py: 1.2,
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.25)}`,
            '&:hover': {
              boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.35)}`,
              transform: 'translateY(-1px)',
            },
            '&.Mui-disabled': {
              bgcolor: COLORS.grey[200],
              color: COLORS.grey[500],
            },
          }}
        >
          {product.status !== 'active' ? 'Out of Stock' : 'Add to Cart'}
        </Button>
        <IconButton
          onClick={() => onView(product)}
          sx={{
            bgcolor: COLORS.grey[100],
            '&:hover': { bgcolor: COLORS.grey[200] },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  </Zoom>
));

// ============================================
// MAIN COMPONENT
// ============================================
const BuyerDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'buyer';

  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('/buyer/dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPrice, setFilterPrice] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [timeframe, setTimeframe] = useState('month');
  const [viewMode, setViewMode] = useState('grid');
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // Data states
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [buyerData, setBuyerData] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [cart, setCart] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    pendingOrders: 0,
    deliveredOrders: 0,
    wishlistItems: 0,
    totalProducts: 0,
  });

  // Dialog states
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutDialog, setCheckoutDialog] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [productDetailsDialog, setProductDetailsDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // ============================================
  // CHART DATA - Computed with useMemo for performance
  // ============================================
  const chartData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthMap = {};
    
    months.forEach(month => {
      monthMap[month] = { month, orders: 0, revenue: 0 };
    });
    
    orders.forEach(order => {
      if (order.order_date) {
        const date = new Date(order.order_date);
        const month = months[date.getMonth()];
        if (monthMap[month]) {
          monthMap[month].orders += 1;
          monthMap[month].revenue += parseFloat(order.total_amount || 0);
        }
      }
    });
    
    return Object.values(monthMap);
  }, [orders]);

  const categoryData = useMemo(() => {
    const categories = {};
    products.forEach(product => {
      const cat = product.productType || 'Uncategorized';
      if (!categories[cat]) {
        categories[cat] = { name: cat, value: 0 };
      }
      categories[cat].value += 1;
    });
    
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#FF8A5C', '#A29BFE'];
    return Object.values(categories).map((cat, i) => ({
      ...cat,
      color: colors[i % colors.length],
    }));
  }, [products]);

  const orderStatusData = useMemo(() => {
    const statusMap = {
      'pending': { name: 'Pending', value: 0, color: COLORS.warning },
      'processing': { name: 'Processing', value: 0, color: COLORS.info },
      'shipped': { name: 'Shipped', value: 0, color: '#6A1B9A' },
      'delivered': { name: 'Delivered', value: 0, color: COLORS.success },
      'cancelled': { name: 'Cancelled', value: 0, color: COLORS.error },
    };
    
    orders.forEach(order => {
      const status = order.status || 'pending';
      if (statusMap[status]) {
        statusMap[status].value += 1;
      }
    });
    
    return Object.values(statusMap);
  }, [orders]);

  // ============================================
  // API FUNCTIONS
  // ============================================
  const fetchBuyerData = useCallback(async () => {
    logger.info('Fetching buyer profile...');
    
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found.');
      }

      const url = `${API_ENDPOINTS.BUYER.PROFILE}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();
      
      if (result.success) {
        setBuyerData(result.data);
        logger.info('Buyer profile loaded successfully');
        return result.data;
      } else {
        logger.warn('Profile API returned error:', result.message);
        return null;
      }
    } catch (error) {
      logger.error('Error fetching buyer data:', error);
      return null;
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    logger.info('Fetching products...');

    try {
      let url = `${API_ENDPOINTS.PRODUCTS.LIST}?limit=50`;
      
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      if (filterCategory && filterCategory !== 'all') {
        url += `&category=${encodeURIComponent(filterCategory)}`;
      }
      
      if (filterPrice && filterPrice !== 'all') {
        switch (filterPrice) {
          case 'under10000':
            url += `&max_price=10000`;
            break;
          case '10000to30000':
            url += `&min_price=10000&max_price=30000`;
            break;
          case '30000to50000':
            url += `&min_price=30000&max_price=50000`;
            break;
          case 'above50000':
            url += `&min_price=50000`;
            break;
          default:
            break;
        }
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();

      if (result.success && result.data) {
        let productsData = [];
        
        if (result.data.products) {
          productsData = result.data.products;
        } else if (Array.isArray(result.data)) {
          productsData = result.data;
        } else if (Array.isArray(result.data.data)) {
          productsData = result.data.data;
        }
        
        setProducts(productsData);
        logger.info(`✅ Loaded ${productsData.length} products`);
        
        setStats(prev => ({
          ...prev,
          totalProducts: productsData.length,
        }));
        
        return productsData;
      } else {
        throw new Error(result.message || 'Failed to fetch products');
      }
    } catch (error) {
      logger.error('Error fetching products:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load products.',
        severity: 'error',
      });
      return [];
    }
  }, [searchTerm, filterCategory, filterPrice]);

  const fetchOrders = useCallback(async (profileData) => {
    logger.info('Fetching orders...');

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found.');
      }

      // Get buyer_id from profile data or fetch it
      let buyerId = profileData?.buyer_id || profileData?.id || buyerData?.buyer_id || buyerData?.id;

      if (!buyerId) {
        // If we still don't have buyerId, fetch profile directly
        const profileResponse = await fetch(`${API_ENDPOINTS.BUYER.PROFILE}?user_id=${userId}`);
        const profileResult = await profileResponse.json();
        if (profileResult.success && profileResult.data) {
          buyerId = profileResult.data.buyer_id || profileResult.data.id;
          if (profileResult.data.buyer_id) {
            setBuyerData(profileResult.data);
          }
        }
      }

      // If still no buyerId, try using userId (fallback for testing)
      if (!buyerId) {
        buyerId = userId;
        logger.warn('Using userId as fallback for buyer_id');
      }

      const url = `${API_ENDPOINTS.ORDERS.LIST}?buyer_id=${buyerId}&user_id=${userId}`;
      logger.info('Fetching orders from URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();

      if (result.success) {
        let ordersData = result.data || [];
        setOrders(ordersData);
        logger.info(`✅ Loaded ${ordersData.length} orders`);
        
        const pending = ordersData.filter(o => o.status === 'pending' || o.status === 'processing').length;
        const delivered = ordersData.filter(o => o.status === 'delivered').length;
        const spent = ordersData
          .filter(o => o.status === 'delivered')
          .reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);
        
        setStats(prev => ({
          ...prev,
          totalOrders: ordersData.length,
          pendingOrders: pending,
          deliveredOrders: delivered,
          totalSpent: spent,
        }));
        
        return ordersData;
      } else {
        throw new Error(result.message || 'Failed to fetch orders');
      }
    } catch (error) {
      logger.error('Error fetching orders:', error);
      setOrders([]);
      return [];
    }
  }, [buyerData]);

  const fetchWishlist = useCallback(async () => {
    logger.info('Fetching wishlist...');

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return [];

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) return [];

      const url = `${API_ENDPOINTS.WISHLIST.GET}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();
      
      if (result.success) {
        const wishlistData = result.data || [];
        setWishlist(wishlistData);
        setStats(prev => ({
          ...prev,
          wishlistItems: wishlistData.length,
        }));
        logger.info(`✅ Loaded ${wishlistData.length} wishlist items`);
        return wishlistData;
      }
      return [];
    } catch (error) {
      logger.error('Error fetching wishlist:', error);
      return [];
    }
  }, []);

  const loadDashboardData = useCallback(async () => {
    logger.info('Loading dashboard data...');
    setLoading(true);
    setLoadingError(null);
    
    try {
      // Fetch profile first to get buyer_id
      const profileData = await fetchBuyerData();
      
      // Then fetch everything else in parallel
      const [productsData, ordersData, wishlistData] = await Promise.all([
        fetchProducts(),
        fetchOrders(profileData),
        fetchWishlist(),
      ]);
      
      setLastRefreshed(new Date());
      logger.info('✅ Dashboard data loaded successfully');
    } catch (error) {
      logger.error('Error loading dashboard data:', error);
      setLoadingError(error.message);
      setSnackbar({
        open: true,
        message: 'Error loading dashboard data. Please refresh.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
      logger.info('Loading complete');
    }
  }, [fetchBuyerData, fetchProducts, fetchOrders, fetchWishlist]);

  // ============================================
  // CART FUNCTIONS
  // ============================================
  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + (parseFloat(item.price || 0) * item.quantity), 0);
  }, [cart]);

  const cartItemCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  const addToCart = useCallback((product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setSnackbar({
      open: true,
      message: `${product.name} added to cart`,
      severity: 'success',
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
    setSnackbar({
      open: true,
      message: 'Item removed from cart',
      severity: 'info',
    });
  }, []);

  const updateQuantity = useCallback((productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // ============================================
  // WISHLIST FUNCTIONS
  // ============================================
  const toggleWishlist = useCallback((product) => {
    setWishlist(prev => {
      const exists = prev.some(item => item.id === product.id);
      if (exists) {
        setSnackbar({
          open: true,
          message: 'Removed from wishlist',
          severity: 'info',
        });
        return prev.filter(item => item.id !== product.id);
      }
      setSnackbar({
        open: true,
        message: 'Added to wishlist',
        severity: 'success',
      });
      return [...prev, product];
    });
  }, []);

  const isInWishlist = useCallback((productId) => {
    return wishlist.some(item => item.id === productId);
  }, [wishlist]);

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || userType !== 'buyer') {
      navigate('/RawAuth');
      return;
    }
    loadDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // FILTERED DATA
  // ============================================
  const filteredProducts = useMemo(() => {
    let filtered = products;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(term) ||
        p.productType?.toLowerCase().includes(term) ||
        p.ManufacturerName?.toLowerCase().includes(term) ||
        p.registryNumber?.toLowerCase().includes(term)
      );
    }
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(p => 
        p.productType?.toLowerCase() === filterCategory.toLowerCase()
      );
    }
    
    if (filterPrice !== 'all') {
      filtered = filtered.filter(p => {
        const price = parseFloat(p.price || 0);
        switch (filterPrice) {
          case 'under10000': return price < 10000;
          case '10000to30000': return price >= 10000 && price <= 30000;
          case '30000to50000': return price > 30000 && price <= 50000;
          case 'above50000': return price > 50000;
          default: return true;
        }
      });
    }
    
    return filtered;
  }, [products, searchTerm, filterCategory, filterPrice]);

  const categories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.productType).filter(Boolean))];
    return ['all', ...cats];
  }, [products]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleTabChange = useCallback((tab) => {
    setSelectedTab(tab);
    navigate(tab);
  }, [navigate]);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleChangePassword = useCallback(() => {
    setChangePasswordDialog(true);
  }, []);

  const handleRefresh = useCallback(() => {
    loadDashboardData();
    setSnackbar({ open: true, message: 'Data refreshed', severity: 'info' });
  }, [loadDashboardData]);

  const handleCheckout = useCallback(() => {
    if (cart.length === 0) {
      setSnackbar({ open: true, message: 'Your cart is empty', severity: 'warning' });
      return;
    }
    setCheckoutDialog(true);
  }, [cart]);

  const handleViewProduct = useCallback((product) => {
    setSelectedProduct(product);
    setProductDetailsDialog(true);
  }, []);

  const handleViewOrder = useCallback((order) => {
    setSelectedOrder(order);
    setOrderDetailsDialog(true);
  }, []);

  const confirmCheckout = useCallback(() => {
    const newOrder = {
      id: `ORD-${String(orders.length + 1).padStart(6, '0')}`,
      supplier: cart[0]?.ManufacturerName || 'Multiple Suppliers',
      items: cart.length,
      total: cartTotal,
      status: 'pending',
      paymentStatus: 'pending',
      order_date: new Date().toISOString(),
      shipping_address: buyerData?.delivery_address || 'Not specified',
      delivery_method: 'Standard',
    };

    setOrders(prev => [newOrder, ...prev]);
    setStats(prev => ({
      ...prev,
      totalOrders: prev.totalOrders + 1,
      totalSpent: prev.totalSpent + cartTotal,
      pendingOrders: prev.pendingOrders + 1,
    }));

    clearCart();
    setCheckoutDialog(false);
    setCartOpen(false);
    setSnackbar({ open: true, message: 'Requests placed successfully!', severity: 'success' });
  }, [cart, cartTotal, orders.length, buyerData, clearCart]);

  // ============================================
  // RENDER FUNCTIONS - Optimized with useCallback
  // ============================================
  const renderStats = useCallback(() => {
    const statCards = [
      {
        icon: <ShoppingBagIcon />,
        title: 'Total Request',
        value: stats.totalOrders,
        subtitle: 'All time orders',
        color: COLORS.primary,
        trend: stats.totalOrders > 0 ? 'up' : null,
        trendValue: stats.totalOrders > 0 ? `${stats.totalOrders} orders` : 'No orders yet',
      },
      {
        icon: <AttachMoneyIcon />,
        title: 'Total Spent',
        value: formatCurrency(stats.totalSpent),
        subtitle: 'Lifetime spend',
        color: COLORS.success,
        trend: stats.totalSpent > 0 ? 'up' : null,
        trendValue: stats.totalSpent > 0 ? 'Active buyer' : 'Start shopping',
      },
      {
        icon: <ScheduleIcon />,
        title: 'Pending Requests',
        value: stats.pendingOrders,
        subtitle: 'Awaiting processing',
        color: COLORS.warning,
        progress: stats.totalOrders > 0 ? (stats.pendingOrders / stats.totalOrders) * 100 : 0,
      },
      {
        icon: <FavoriteIcon />,
        title: 'Wishlist',
        value: wishlist.length,
        subtitle: 'Saved items',
        color: COLORS.error,
        trend: wishlist.length > 0 ? 'up' : null,
        trendValue: wishlist.length > 0 ? `${wishlist.length} items saved` : 'Add items to wishlist',
      },
    ];

    return (
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid size={{ xs: 6, sm: 6, lg: 3 }} key={index}>
            <GradientStatCard {...card} />
          </Grid>
        ))}
      </Grid>
    );
  }, [stats, wishlist]);

  const renderCharts = useCallback(() => {
    const totalRevenue = chartData.reduce((sum, item) => sum + item.revenue, 0);
    const totalOrders = chartData.reduce((sum, item) => sum + item.orders, 0);

    return (
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        {/* Revenue Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>Requests & Overview</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Monthly Requests and trends
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {['Week', 'Month', 'Year'].map((label) => (
                    <Button
                      key={label}
                      size="small"
                      variant={timeframe === label.toLowerCase() ? 'contained' : 'outlined'}
                      onClick={() => setTimeframe(label.toLowerCase())}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        px: { xs: 1, sm: 2 },
                        ...(timeframe === label.toLowerCase() && {
                          bgcolor: COLORS.primary,
                          '&:hover': { bgcolor: COLORS.primaryLight },
                        }),
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
              </Box>
              <Box sx={{ height: { xs: 200, sm: 250, md: 300 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.01}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey[200]} vertical={false} />
                    <XAxis 
                      dataKey="month" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#9e9e9e', fontSize: { xs: 8, sm: 10, md: 11 } }}
                      interval={isSmallScreen ? 1 : 0}
                    />
                    <YAxis 
                      yAxisId="left" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#9e9e9e', fontSize: { xs: 8, sm: 10, md: 11 } }}
                      tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#9e9e9e', fontSize: { xs: 8, sm: 10, md: 11 } }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: 'none',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        background: 'rgba(255,255,255,0.95)',
                      }}
                      formatter={(value, name) => {
                        if (name === 'Revenue') return [`₦${value.toLocaleString()}`, name];
                        return [value, name];
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: 10 }}
                      iconType="circle"
                      iconSize={8}
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      stroke={COLORS.primary}
                      strokeWidth={3}
                      fill="url(#revenueGradient)"
                      name="Revenue (₦)"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="orders"
                      fill={COLORS.accent}
                      radius={[4, 4, 0, 0]}
                      name="Orders"
                      barSize={isSmallScreen ? 16 : 30}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Category Distribution */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ borderRadius: 4, overflow: 'hidden', height: '100%', border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon sx={{ color: COLORS.primary }} />
                Category Distribution
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                Products by category
              </Typography>
              <Box sx={{ height: { xs: 200, sm: 220, md: 260 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={isSmallScreen ? 40 : 60}
                      outerRadius={isSmallScreen ? 60 : 90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                      fontSize={isSmallScreen ? 8 : 11}
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || '#45B7D1'} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: 'none',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mt: 1 }}>
                {categoryData.slice(0, 4).map((cat, i) => (
                  <Chip
                    key={i}
                    label={`${cat.name} (${cat.value})`}
                    size="small"
                    sx={{
                      bgcolor: alpha(cat.color || '#45B7D1', 0.1),
                      color: cat.color || '#45B7D1',
                      fontWeight: 600,
                      fontSize: '0.65rem',
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Status */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingIcon sx={{ color: COLORS.primary }} />
                Requests Status
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                Current Requests status breakdown
              </Typography>
              <Box sx={{ height: { xs: 150, sm: 180, md: 200 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={orderStatusData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey[200]} vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: { xs: 8, sm: 10, md: 11 } }} />
                    <YAxis axisLine={false} tickLine={false} />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: 'none',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={isSmallScreen ? 20 : 40}>
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || '#45B7D1'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Orders */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon sx={{ color: COLORS.accent }} />
                    Recent Requests
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Latest Requests from your history
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => handleTabChange('/buyer/orders')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  View All
                </Button>
              </Box>
              <Box sx={{ maxHeight: { xs: 200, sm: 240, md: 280 }, overflow: 'auto' }}>
                {orders.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      No Requests yet
                    </Typography>
                  </Box>
                ) : (
                  orders.slice(0, 5).map((order, index) => {
                    const statusColor = getStatusColor(order.status);
                    return (
                      <Fade in key={order.id || index} style={{ transitionDelay: `${index * 50}ms` }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            mb: 1,
                            borderRadius: 2,
                            border: `1px solid ${COLORS.grey[200]}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(COLORS.primary, 0.02),
                              borderColor: COLORS.primary,
                            },
                          }}
                          onClick={() => handleViewOrder(order)}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(statusColor, 0.12),
                              color: statusColor,
                              width: 40,
                              height: 40,
                            }}
                          >
                            <ReceiptIcon sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              Requests #{order.id || index + 1}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="caption" color="textSecondary">
                                {formatCurrency(order.total_amount || 0)}
                              </Typography>
                              <StatusChip
                                label={getStatusLabel(order.status)}
                                status={statusColor}
                                size="small"
                              />
                            </Box>
                          </Box>
                          <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(order.order_date)}
                          </Typography>
                        </Box>
                      </Fade>
                    );
                  })
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }, [chartData, categoryData, orderStatusData, orders, timeframe, isSmallScreen, handleTabChange, handleViewOrder]);

  // ============================================
// RENDER FUNCTIONS - Add these before renderDashboard
// ============================================

const renderMarketplace = useCallback(() => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
      <Box>
        <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <StorefrontIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
          Marketplace
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Browse and purchase quality chemicals
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          onClick={() => setCartOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
          }}
        >
          Cart ({cartItemCount})
        </Button>
      </Box>
    </Box>

    {/* Filters */}
    <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search products by name, category, supplier..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: COLORS.grey[500] }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" sx={{ color: COLORS.grey[500] }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="all" sx={{ fontWeight: 600 }}>All Categories</MenuItem>
            {categories.filter(c => c !== 'all').map(cat => (
              <MenuItem key={cat} value={cat} sx={{ fontWeight: 500 }}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={filterPrice}
            onChange={(e) => setFilterPrice(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="all" sx={{ fontWeight: 600 }}>All Prices</MenuItem>
            <MenuItem value="under10000" sx={{ fontWeight: 500 }}>Under ₦10,000</MenuItem>
            <MenuItem value="10000to30000" sx={{ fontWeight: 500 }}>₦10,000 - ₦30,000</MenuItem>
            <MenuItem value="30000to50000" sx={{ fontWeight: 500 }}>₦30,000 - ₦50,000</MenuItem>
            <MenuItem value="above50000" sx={{ fontWeight: 500 }}>Above ₦50,000</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Chip 
            label={`${filteredProducts.length} products`}
            sx={{ 
              bgcolor: alpha(COLORS.primary, 0.08), 
              color: COLORS.primary,
              fontWeight: 600,
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setViewMode('grid')}
              sx={{
                bgcolor: viewMode === 'grid' ? alpha(COLORS.primary, 0.08) : 'transparent',
                color: viewMode === 'grid' ? COLORS.primary : COLORS.grey[400],
              }}
            >
              <GridIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setViewMode('list')}
              sx={{
                bgcolor: viewMode === 'list' ? alpha(COLORS.primary, 0.08) : 'transparent',
                color: viewMode === 'list' ? COLORS.primary : COLORS.grey[400],
              }}
            >
              <ListIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Paper>

    {/* Products Grid */}
    <Grid container spacing={3}>
      {filteredProducts.length === 0 ? (
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <InventoryIcon sx={{ fontSize: 64, color: COLORS.grey[400], mb: 2 }} />
            <Typography variant="h6" color="textSecondary" fontWeight={600}>
              No products found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Try adjusting your search or filters
            </Typography>
          </Paper>
        </Grid>
      ) : (
        filteredProducts
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((product) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={product.id}>
              <ProductCardComponent
                product={product}
                onAddToCart={addToCart}
                onToggleWishlist={toggleWishlist}
                isInWishlist={isInWishlist}
                onView={handleViewProduct}
              />
            </Grid>
          ))
      )}
    </Grid>
    
    <TablePagination
      rowsPerPageOptions={[8, 12, 24, 48]}
      component="div"
      count={filteredProducts.length}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={(e, newPage) => setPage(newPage)}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
      sx={{ mt: 3 }}
    />
  </Box>
), [
  filteredProducts, 
  categories, 
  searchTerm, 
  filterCategory, 
  filterPrice, 
  viewMode, 
  page, 
  rowsPerPage, 
  cartItemCount, 
  addToCart, 
  toggleWishlist, 
  isInWishlist, 
  handleViewProduct, 
  handleRefresh, 
  setCartOpen,
  setSearchTerm,
  setFilterCategory,
  setFilterPrice,
  setViewMode,
  setPage,
  setRowsPerPage
]);

const renderOrders = useCallback(() => (
  <Box>
    <Box sx={{ mb: 3 }}>
      <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ReceiptIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
        My Requests
      </Typography>
      <Typography variant="body1" color="textSecondary">
        Track and manage your Requests
      </Typography>
    </Box>

    <Grid container spacing={3} sx={{ mb: 3 }}>
      {[
        { label: 'Total Orders', value: stats.totalOrders, color: COLORS.primary, icon: <ShoppingBagIcon /> },
        { label: 'Pending', value: stats.pendingOrders, color: COLORS.warning, icon: <ScheduleIcon /> },
        { label: 'Delivered', value: stats.deliveredOrders, color: COLORS.success, icon: <CheckCircleIcon /> },
        { label: 'Cancelled', value: orders.filter(o => o.status === 'cancelled').length, color: COLORS.error, icon: <CancelIcon /> },
      ].map((stat, index) => (
        <Grid size={{ xs: 6, sm: 3 }} key={index}>
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: alpha(stat.color, 0.05),
              border: `1px solid ${alpha(stat.color, 0.1)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px ${alpha(stat.color, 0.15)}`,
              },
            }}
          >
            <CardContent sx={{ p: 2, textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  mx: 'auto',
                  mb: 1,
                  bgcolor: alpha(stat.color, 0.1),
                  color: stat.color,
                }}
              >
                {stat.icon}
              </Avatar>
              <Typography variant="h4" fontWeight={700} sx={{ color: stat.color }}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="textSecondary" fontWeight={600}>
                {stat.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${COLORS.grey[200]}` }}>
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Requests ID</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Supplier</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Items</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }} align="right">Total</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Payment</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }} align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary" fontWeight={500}>No Requests yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order) => {
                  const statusColor = getStatusColor(order.status);
                  const paymentColor = getStatusColor(order.payment_status);
                  return (
                    <TableRow key={order.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewOrder(order)}>
                      <TableCell>
                        <Typography fontWeight={700} color={COLORS.grey[800]}>
                          #{order.id || `ORD-${order.order_date?.slice(0, 10)}`}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.supplier_name || order.ManufacturerName || 'N/A'}</TableCell>
                      <TableCell>{order.items || 1} item(s)</TableCell>
                      <TableCell align="right" fontWeight={700} color={COLORS.primary}>
                        {formatCurrency(order.total_amount || 0)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(order.status)}
                          size="small"
                          sx={{
                            bgcolor: alpha(statusColor, 0.1),
                            color: statusColor,
                            fontWeight: 600,
                            border: `1px solid ${alpha(statusColor, 0.15)}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(order.payment_status)}
                          size="small"
                          sx={{
                            bgcolor: alpha(paymentColor, 0.1),
                            color: paymentColor,
                            fontWeight: 600,
                            border: `1px solid ${alpha(paymentColor, 0.15)}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={500}>
                          {formatDate(order.order_date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewOrder(order);
                            }}
                            sx={{ color: COLORS.primary }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={orders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  </Box>
), [orders, stats, page, rowsPerPage, handleViewOrder]);

const renderWishlist = useCallback(() => (
  <Box>
    <Box sx={{ mb: 3 }}>
      <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <FavoriteIcon sx={{ color: COLORS.error, fontSize: 32 }} />
        My Wishlist
      </Typography>
      <Typography variant="body1" color="textSecondary">
        Products you've saved for later
      </Typography>
    </Box>

    {wishlist.length === 0 ? (
      <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
        <FavoriteBorderIcon sx={{ fontSize: 64, color: COLORS.grey[300], mb: 2 }} />
        <Typography variant="h6" fontWeight={600} color="textSecondary">
          Your wishlist is empty
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Start adding products you're interested in
        </Typography>
        <Button
          variant="contained"
          onClick={() => handleTabChange('/buyer/marketplace')}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
          }}
        >
          Browse Products
        </Button>
      </Paper>
    ) : (
      <Grid container spacing={3}>
        {wishlist.map((product) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={product.id}>
            <ProductCardComponent
              product={product}
              onAddToCart={addToCart}
              onToggleWishlist={toggleWishlist}
              isInWishlist={() => true}
              onView={handleViewProduct}
            />
          </Grid>
        ))}
      </Grid>
    )}
  </Box>
), [wishlist, addToCart, toggleWishlist, handleViewProduct, handleTabChange]);

const renderSuppliers = useCallback(() => (
  <Box>
    <Box sx={{ mb: 3 }}>
      <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <BusinessIcon sx={{ color: COLORS.info, fontSize: 32 }} />
        Suppliers
      </Typography>
      <Typography variant="body1" color="textSecondary">
        Verified chemical suppliers
      </Typography>
    </Box>

    <Grid container spacing={3}>
      {products.length === 0 ? (
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
            <Typography variant="body1" color="textSecondary">No suppliers available</Typography>
          </Paper>
        </Grid>
      ) : (
        // Get unique suppliers from products
        [...new Set(products.map(p => p.ManufacturerName || p.supplier_name).filter(Boolean))].map((supplierName, index) => {
          const supplierProducts = products.filter(p => (p.ManufacturerName || p.supplier_name) === supplierName);
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card sx={{ 
                borderRadius: 4, 
                border: `1px solid ${COLORS.grey[200]}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.08)',
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        width: 48, 
                        height: 48,
                        bgcolor: alpha(COLORS.primary, 0.1),
                        color: COLORS.primary,
                        fontWeight: 700,
                      }}
                    >
                      {supplierName[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {supplierName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOnIcon sx={{ fontSize: 14, color: COLORS.grey[400] }} />
                        <Typography variant="caption" color="textSecondary">
                          {supplierProducts[0]?.CountryOrigin || 'Various Locations'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>
                        Products
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {supplierProducts.length}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight={600}>
                        Price Range
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color={COLORS.primary}>
                        {formatCurrency(Math.min(...supplierProducts.map(p => parseFloat(p.price))))} - 
                        {formatCurrency(Math.max(...supplierProducts.map(p => parseFloat(p.price))))}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        setSearchTerm(supplierName);
                        handleTabChange('/buyer/marketplace');
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: COLORS.primary,
                        color: COLORS.primary,
                        '&:hover': {
                          bgcolor: alpha(COLORS.primary, 0.04),
                        },
                      }}
                    >
                      View Products
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })
      )}
    </Grid>
  </Box>
), [products, handleTabChange, setSearchTerm]);

const renderSettings = useCallback(() => (
  <Box>
    <Box sx={{ mb: 3 }}>
      <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SettingsIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
        Settings
      </Typography>
      <Typography variant="body1" color="textSecondary">
        Manage your account and preferences
      </Typography>
    </Box>

    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Profile Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField 
                label="Full Name" 
                defaultValue={buyerData?.name || userData?.name || ''} 
                fullWidth 
                size="medium"
              />
              <TextField 
                label="Email" 
                defaultValue={buyerData?.email || userData?.email || ''} 
                fullWidth 
                size="medium"
                disabled
              />
              <TextField 
                label="Phone" 
                defaultValue={buyerData?.phone || userData?.phone || ''} 
                fullWidth 
                size="medium"
              />
              <TextField 
                label="Organisation" 
                defaultValue={buyerData?.organisation || ''} 
                fullWidth 
                size="medium"
              />
              <TextField 
                label="Industry" 
                defaultValue={buyerData?.industry || ''} 
                fullWidth 
                size="medium"
              />
              <TextField 
                label="Delivery Address" 
                defaultValue={buyerData?.delivery_address || ''} 
                fullWidth 
                multiline
                rows={2}
                size="medium"
              />
              <Button 
                variant="contained"
                sx={{
                  bgcolor: COLORS.primary,
                  '&:hover': { bgcolor: COLORS.primaryLight },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  mt: 1,
                }}
              >
                Save Changes
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Security & Preferences
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<LockOutlinedIcon />}
                onClick={handleChangePassword}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none', 
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                  borderColor: COLORS.grey[300],
                }}
                fullWidth
              >
                Change Password
              </Button>
              <Divider />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={<Typography fontWeight={500}>Email Notifications</Typography>}
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={<Typography fontWeight={500}>Requests Updates</Typography>}
              />
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={<Typography fontWeight={500}>Promotional Offers</Typography>}
              />
              <FormControlLabel
                control={<Switch />}
                label={<Typography fontWeight={500}>Newsletter Subscription</Typography>}
              />
              <Divider sx={{ my: 1 }} />
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none', 
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                }}
                fullWidth
              >
                Logout
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Box>
), [buyerData, userData, handleChangePassword, handleLogout]);

  const renderDashboard = useCallback(() => (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Welcome 
              {buyerData?.user_verified && (
                <Tooltip title="Verified Account">
                  <VerifiedIcon sx={{ color: COLORS.success, fontSize: 28 }} />
                </Tooltip>
              )}
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mt: 0.5, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Here's your chemical database dashboard
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ borderRadius: 2, textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Refresh
            </Button>
            {/* <Button
              variant="contained"
              startIcon={<ShoppingCartIcon />}
              onClick={() => setCartOpen(true)}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                '&:hover': {
                  background: `linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary})`,
                },
              }}
            >
              Cart ({cartItemCount})
            </Button> */}
          </Box>
        </Box>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          Last updated: {lastRefreshed.toLocaleString()}
        </Typography>
      </Box>

      {renderStats()}
      {renderCharts()}
    </Box>
  ), [buyerData, userData, cartItemCount, handleRefresh, renderStats, renderCharts]);

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading && products.length === 0 && orders.length === 0) {
    return <Preloader text="Loading Dashboard..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>
      <Sidebar
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isMobile={isMobile}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        userData={userData}
        wishlistCount={wishlist.length}
        cartItemCount={cartItemCount}
        onLogout={handleLogout}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          cartItemCount={cartItemCount}
          onCartOpen={() => setCartOpen(true)}
          notifications={[]}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={handleChangePassword}
          onSettings={() => handleTabChange('/buyer/settings')}
        />

        <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2, md: 3 }, overflow: 'auto' }}>
          {selectedTab === '/buyer/dashboard' && renderDashboard()}
          {selectedTab === '/buyer/marketplace' && renderMarketplace()}
          {selectedTab === '/buyer/orders' && renderOrders()}
          {selectedTab === '/buyer/wishlist' && renderWishlist()}
          {selectedTab === '/buyer/suppliers' && renderSuppliers()}
          {selectedTab === '/buyer/settings' && renderSettings()}
        </Box>

        <Footer />
      </Box>

      {/* Cart Drawer - Keep as is */}
      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            p: 2.5,
            borderTopLeftRadius: { sm: 16 },
            borderBottomLeftRadius: { sm: 16 },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon sx={{ color: COLORS.primary }} />
            Shopping Cart
            <Chip
              label={`${cartItemCount} items`}
              size="small"
              sx={{ ml: 1, fontWeight: 600 }}
            />
          </Typography>
          <IconButton onClick={() => setCartOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {cart.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ShoppingCartIcon sx={{ fontSize: 64, color: COLORS.grey[300], mb: 2 }} />
            <Typography variant="h6" fontWeight={600} color="textSecondary">
              Your cart is empty
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Start shopping to add items
            </Typography>
            <Button
              variant="contained"
              onClick={() => setCartOpen(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
              }}
            >
              Browse Products
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
              {cart.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    p: 1.5,
                    mb: 1,
                    borderRadius: 3,
                    border: `1px solid ${COLORS.grey[200]}`,
                    bgcolor: COLORS.grey[50],
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: COLORS.primary,
                      bgcolor: 'white',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={getImageUrl(item.image) || `https://picsum.photos/seed/${item.id}/60/60`}
                    alt={item.name}
                    sx={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 2,
                      bgcolor: COLORS.grey[100],
                    }}
                    onError={(e) => {
                      e.target.src = `https://picsum.photos/seed/${item.id}/60/60`;
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {formatCurrency(item.price, item.currency)} / {item.unit_of_measure || 'unit'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <IconButton
                        size="small"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        sx={{ border: `1px solid ${COLORS.grey[200]}`, borderRadius: 1 }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2" fontWeight={700} sx={{ minWidth: 24, textAlign: 'center' }}>
                        {item.quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        sx={{ border: `1px solid ${COLORS.grey[200]}`, borderRadius: 1 }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2" fontWeight={700} sx={{ ml: 'auto', color: COLORS.primary }}>
                        {formatCurrency(parseFloat(item.price || 0) * item.quantity, item.currency)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeFromCart(item.id)}
                        sx={{ color: COLORS.error }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="textSecondary" fontWeight={500}>
                  Subtotal
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {formatCurrency(cartTotal)}
                </Typography>
              </Box>
              <Button
                fullWidth
                variant="contained"
                onClick={handleCheckout}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                  boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.25)}`,
                  '&:hover': {
                    boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.35)}`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Proceed to Checkout (₦{cartTotal.toLocaleString()})
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => setCartOpen(false)}
                sx={{ mt: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Continue Shopping
              </Button>
            </Box>
          </>
        )}
      </Drawer>

      {/* Dialogs */}
      <Dialog
        open={checkoutDialog}
        onClose={() => setCheckoutDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={800}>Confirm Order</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to place a Request for {cart.length} item(s) totaling <strong>{formatCurrency(cartTotal)}</strong>.
          </DialogContentText>
          <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
            {cart.map((item, index) => (
              <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, py: 0.5, borderBottom: index < cart.length - 1 ? `1px solid ${COLORS.grey[200]}` : 'none' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {index + 1}. {item.name} × {item.quantity}
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {formatCurrency(parseFloat(item.price || 0) * item.quantity, item.currency)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
              <Typography variant="h6" fontWeight={800} color="primary">{formatCurrency(cartTotal)}</Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setCheckoutDialog(false)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmCheckout}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: `linear-gradient(135deg, ${COLORS.success}, ${COLORS.success}dd)`,
              boxShadow: `0 4px 16px ${alpha(COLORS.success, 0.25)}`,
              '&:hover': {
                boxShadow: `0 8px 24px ${alpha(COLORS.success, 0.35)}`,
              },
            }}
          >
            Place Order
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={changePasswordDialog}
        onClose={() => setChangePasswordDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LockOutlinedIcon sx={{ color: COLORS.primary }} />
            <Typography variant="h6" fontWeight={800}>Change Password</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField label="Current Password" type="password" fullWidth size="medium" />
            <TextField label="New Password" type="password" fullWidth size="medium" />
            <TextField label="Confirm New Password" type="password" fullWidth size="medium" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setChangePasswordDialog(false)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            }}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 3, boxShadow: '0 16px 48px rgba(0,0,0,0.12)', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Global styles */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
          }
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          ::-webkit-scrollbar-track {
            background: ${COLORS.grey[100]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background: ${COLORS.grey[300]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: ${COLORS.grey[400]};
          }
        `}
      </style>
    </Box>
  );
};

export default BuyerDashboard;